import { useCallback, useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	MenuItem,
	Paper,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import FileDownloadRounded from "@mui/icons-material/FileDownloadRounded";
import ExcelJS from "exceljs";
import FileSaver from "file-saver";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";
import GigaVolumeChart, { GIGA_PALETTE, GigaClusterBubbleChart } from "./GigaVolumeChart";
import { COLUMN_HELP, GROWTH_METRICS, headerWithInfo, InfoTip, MetricGuideButton } from "./GigaMetricGuide";

export const pct = (value) => {
	if (value == null || Number.isNaN(Number(value))) return "-";
	return `${(Number(value) * 100).toFixed(0)}%`;
};

const COMPETITION_BY_NUMBER = { 0: "UNSPECIFIED", 1: "UNKNOWN", 2: "LOW", 3: "MEDIUM", 4: "HIGH" };

export const formatCompetition = (value) => {
	if (value == null || value === "") return "";
	const text = String(value).split(".").pop().toUpperCase();
	if (Object.values(COMPETITION_BY_NUMBER).includes(text)) return text;
	return COMPETITION_BY_NUMBER[Number(text)] || String(value);
};

const competitionChipSx = (label) => {
	if (label === "HIGH") return { bgcolor: "#FEF3F2", color: "#B42318", border: "1px solid #FECDCA" };
	if (label === "MEDIUM") return { bgcolor: "#FFFAEB", color: "#B54708", border: "1px solid #FEDF89" };
	if (label === "LOW") return { bgcolor: "#ECFDF3", color: "#067647", border: "1px solid #ABEFC6" };
	return { bgcolor: grey[50], color: grey[700], border: `1px solid ${grey[200]}` };
};

const growthFromVolumes = (volumes = []) => {
	const latest = volumes[volumes.length - 1] || 0;
	const prevMonth = volumes[volumes.length - 2] || 0;
	const prevYear = volumes[volumes.length - 13] || volumes[0] || 0;
	const total = volumes.reduce((sum, value) => sum + Number(value || 0), 0);
	const avg = volumes.length ? total / volumes.length : 0;
	const historyWithoutLatest = volumes.slice(0, -1);
	const maxPrev = historyWithoutLatest.length ? Math.max(...historyWithoutLatest) : 0;
	const last3 = volumes.slice(-3);
	const prev = volumes.length > 3 ? volumes.slice(0, -3) : [];
	const avgLast3 = last3.length ? last3.reduce((sum, value) => sum + Number(value || 0), 0) / last3.length : 0;
	const avgPrev = prev.length ? prev.reduce((sum, value) => sum + Number(value || 0), 0) / prev.length : 0;
	return {
		growthYoY: prevYear ? (latest - prevYear) / prevYear : 0,
		growthMoM: prevMonth ? (latest - prevMonth) / prevMonth : 0,
		growthLatestVsAvg: avg ? (latest - avg) / avg : 0,
		growthLatestVsMax: maxPrev ? (latest - maxPrev) / maxPrev : 0,
		growthThreeMonthsVsAvg: avgPrev ? (avgLast3 - avgPrev) / avgPrev : 0,
		latestSearchVolume: latest,
	};
};

const sumSeries = (series) => {
	if (!series.length) return [];
	const width = Math.max(...series.map((row) => row.length));
	const totals = Array(width).fill(0);
	series.forEach((row) => {
		row.forEach((value, index) => {
			totals[index] += Number(value || 0);
		});
	});
	return totals;
};

const fallbackClustersFromIdeas = (ideas) => {
	const groups = new Map();
	ideas.forEach((idea) => {
		const words = String(idea.text || "")
			.trim()
			.split(/\s+/)
			.filter(Boolean);
		const topic = (words.slice(0, Math.min(2, words.length)).join(" ") || idea.text || "Other").toLowerCase();
		if (!groups.has(topic)) groups.set(topic, []);
		groups.get(topic).push(idea);
	});
	return [...groups.entries()]
		.sort((a, b) => b[1].length - a[1].length)
		.slice(0, 20)
		.map(([topic, rows]) => {
			const kept = rows.slice(0, 10);
			const history = sumSeries(kept.map((row) => row.searchVolume || []));
			const growth = growthFromVolumes(history);
			return {
				topic,
				keywords: kept.map((row) => row.text),
				searchVolumeHistory: history,
				searchVolume: growth.latestSearchVolume,
				...growth,
			};
		})
		.filter((cluster) => cluster.keywords.length);
};

const growthPct = (value) => {
	if (value == null || Number.isNaN(Number(value))) return "";
	return Number((Number(value) * 100).toFixed(2));
};

const pickField = (row, ...keys) => {
	for (const key of keys) {
		const value = row?.[key];
		if (value != null && value !== "") return value;
	}
	return null;
};

const fromMicros = (value) => {
	if (value == null || value === "") return "";
	const number = Number(value);
	if (!Number.isFinite(number)) return "";
	return Number((number / 1_000_000).toFixed(2));
};

const listCell = (value) => {
	if (Array.isArray(value)) return value.filter(Boolean).join("; ");
	return value || "";
};

const monthHeadersFor = (ideas) => {
	const seen = new Set();
	const headers = [];
	ideas.forEach((idea) => {
		(idea.months || []).forEach((label) => {
			const key = String(label);
			if (!seen.has(key)) {
				seen.add(key);
				headers.push(key);
			}
		});
	});
	return headers;
};

const volumeForMonth = (idea, month) => {
	const months = idea.months || [];
	const index = months.findIndex((label) => String(label) === String(month));
	if (index < 0) return "";
	return Number((idea.searchVolume || [])[index] || 0);
};

const plannerFields = (idea = {}) => ({
	avgMonthlySearches: Number(pickField(idea, "avgMonthlySearches", "avg_monthly_searches") || 0),
	competition: pickField(idea, "competition") ? formatCompetition(pickField(idea, "competition")) : "",
	competitionIndex: pickField(idea, "competitionIndex", "competition_index"),
	lowBid: fromMicros(pickField(idea, "lowTopOfPageBidMicros", "low_top_of_page_bid_micros")),
	highBid: fromMicros(pickField(idea, "highTopOfPageBidMicros", "high_top_of_page_bid_micros")),
	averageCpc: fromMicros(pickField(idea, "averageCpcMicros", "average_cpc_micros")),
	closeVariants: listCell(pickField(idea, "closeVariants", "close_variants") || []),
	concepts: listCell(pickField(idea, "concepts") || []),
});

const addSheet = (workbook, name, headers, rows) => {
	const sheet = workbook.addWorksheet(name);
	sheet.columns = headers.map((header) => ({
		header,
		width: Math.min(28, Math.max(14, String(header).length + 2)),
	}));
	rows.forEach((row) => sheet.addRow(row));
	sheet.getRow(1).font = { bold: true };
};

const downloadExploreExcel = async ({ runName, ideas, clusters }) => {
	const clusterByKeyword = new Map();
	clusters.forEach((cluster) => {
		(cluster.keywords || []).forEach((keyword) => {
			const key = String(keyword || "").toLowerCase();
			const list = clusterByKeyword.get(key) || [];
			list.push(cluster.topic);
			clusterByKeyword.set(key, list);
		});
	});

	const ideaByText = new Map();
	ideas.forEach((idea) => ideaByText.set(String(idea.text || "").toLowerCase(), idea));
	const monthHeaders = monthHeadersFor(ideas);
	const plannerMetricHeaders = [
		"Avg monthly searches",
		"Competition (Low/Med/High)",
		"Competition index (0-100)",
		"Low top of page bid",
		"High top of page bid",
		"Average CPC",
		"Close variants",
		"Concepts",
	];
	const plannerMetricValues = (idea) => {
		const fields = plannerFields(idea);
		return [
			fields.avgMonthlySearches,
			fields.competition,
			fields.competitionIndex ?? "",
			fields.lowBid,
			fields.highBid,
			fields.averageCpc,
			fields.closeVariants,
			fields.concepts,
		];
	};

	const workbook = new ExcelJS.Workbook();
	addSheet(
		workbook,
		"Keyword Planner",
		["Keyword", "Clusters", ...plannerMetricHeaders, "Latest volume", ...monthHeaders],
		ideas.map((idea) => [
			idea.text,
			(clusterByKeyword.get(String(idea.text || "").toLowerCase()) || []).join("; "),
			...plannerMetricValues(idea),
			Number(idea.latestSearchVolume || 0),
			...monthHeaders.map((month) => volumeForMonth(idea, month)),
		]),
	);
	addSheet(
		workbook,
		"Keywords",
		["Keyword", "Clusters", "Latest volume", "Avg monthly", "YoY %", "MoM %", "Latest vs avg %", "Latest vs max %", "3m vs avg %", "Competition (Low/Med/High)"],
		ideas.map((idea) => [
			idea.text,
			(clusterByKeyword.get(String(idea.text || "").toLowerCase()) || []).join("; "),
			Number(idea.latestSearchVolume || 0),
			Number(idea.avgMonthlySearches || 0),
			growthPct(idea.growthYoY),
			growthPct(idea.growthMoM),
			growthPct(idea.growthLatestVsAvg),
			growthPct(idea.growthLatestVsMax),
			growthPct(idea.growthThreeMonthsVsAvg),
			idea.competition ? formatCompetition(idea.competition) : "",
		]),
	);
	addSheet(
		workbook,
		"Clusters",
		["Topic", "Keywords", "Keyword count", "Search volume", "YoY %", "MoM %", "Latest vs avg %", "Latest vs max %", "3m vs avg %"],
		clusters.map((cluster) => [
			cluster.topic,
			(cluster.keywords || []).join("; "),
			(cluster.keywords || []).length,
			Number(cluster.searchVolume || 0),
			growthPct(cluster.growthYoY),
			growthPct(cluster.growthMoM),
			growthPct(cluster.growthLatestVsAvg),
			growthPct(cluster.growthLatestVsMax),
			growthPct(cluster.growthThreeMonthsVsAvg),
		]),
	);
	addSheet(
		workbook,
		"Cluster keywords",
		[
			"Cluster",
			"Keyword",
			"Latest volume",
			"Cluster volume",
			...plannerMetricHeaders,
			"Keyword YoY %",
			"Cluster YoY %",
			"Keyword 3m vs avg %",
			"Cluster 3m vs avg %",
		],
		clusters.flatMap((cluster) =>
			(cluster.keywords || []).map((keyword) => {
				const idea = ideaByText.get(String(keyword).toLowerCase()) || {};
				return [
					cluster.topic,
					keyword,
					Number(idea.latestSearchVolume || 0),
					Number(cluster.searchVolume || 0),
					...plannerMetricValues(idea),
					growthPct(idea.growthYoY),
					growthPct(cluster.growthYoY),
					growthPct(idea.growthThreeMonthsVsAvg),
					growthPct(cluster.growthThreeMonthsVsAvg),
				];
			}),
		),
	);

	const buffer = await workbook.xlsx.writeBuffer();
	const safeName = String(runName || "giga-explore")
		.replace(/[^\w\-]+/g, "_")
		.slice(0, 40);
	FileSaver.saveAs(
		new Blob([buffer], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		}),
		`${safeName}_explore.xlsx`,
	);
};

const growthColor = (value) => {
	const number = Number(value);
	if (!Number.isFinite(number) || number === 0) return grey[700];
	return number > 0 ? "#067647" : "#B42318";
};

const GrowthCell = ({ value }) => (
	<Typography fontSize={13} fontWeight={600} color={growthColor(value)} textAlign="right">
		{pct(value)}
	</Typography>
);

const MoneyCell = ({ value }) => {
	const amount = fromMicros(value);
	return (
		<Typography fontSize={13} textAlign="right">
			{amount === "" ? "-" : Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
		</Typography>
	);
};

const fieldSx = {
	"& .MuiOutlinedInput-root": {
		bgcolor: "#fff",
		borderRadius: 1.5,
		fontSize: 13.5,
		height: 40,
	},
};

const quietBtnSx = {
	textTransform: "none",
	fontWeight: 600,
	fontSize: 12.5,
	height: 32,
	px: 1.25,
	borderRadius: 1.5,
	color: grey[800],
	bgcolor: "#fff",
	border: `1px solid ${grey[300]}`,
	boxShadow: "none",
	"&:hover": { bgcolor: grey[50], borderColor: grey[400], boxShadow: "none" },
};

const GigaExploreView = ({ run }) => {
	const ideas = useMemo(() => (Array.isArray(run?.ideas) ? run.ideas : []), [run]);
	const storedClusters = useMemo(() => (Array.isArray(run?.clusters) ? run.clusters : []), [run]);
	const seeds = useMemo(() => (Array.isArray(run?.seeds) ? run.seeds : []), [run]);
	const clusters = useMemo(
		() => (storedClusters.length ? storedClusters : fallbackClustersFromIdeas(ideas)),
		[storedClusters, ideas],
	);
	const usedFallbackClusters = storedClusters.length === 0 && clusters.length > 0;
	const [growthMetric, setGrowthMetric] = useState("three_months_vs_avg");
	const [selectedCluster, setSelectedCluster] = useState(null);
	const [selectedIdea, setSelectedIdea] = useState(null);
	const [clusterOnly, setClusterOnly] = useState(false);
	const [pageData, setPageData] = useState([]);
	const chartLabels = ideas[0]?.months || [];
	const metric = GROWTH_METRICS.find((item) => item.id === growthMetric) || GROWTH_METRICS[4];

	const ideaByText = useMemo(() => {
		const map = new Map();
		ideas.forEach((idea) => map.set(String(idea.text || "").toLowerCase(), idea));
		return map;
	}, [ideas]);

	const tableIdeas = useMemo(() => {
		if (!clusterOnly || !selectedCluster) return ideas;
		return (selectedCluster.keywords || [])
			.map((text) => ideaByText.get(String(text).toLowerCase()))
			.filter(Boolean);
	}, [clusterOnly, selectedCluster, ideas, ideaByText]);

	const seedSeries = useMemo(() => {
		const seedKeys = new Set(seeds.map((item) => String(item).toLowerCase()));
		return ideas
			.filter((idea) => seedKeys.has(String(idea.text || "").toLowerCase()))
			.map((idea) => ({ name: idea.text, data: idea.searchVolume || [] }));
	}, [ideas, seeds]);

	const clusterSeries = useMemo(() => {
		if (!selectedCluster) return [];
		return [
			{
				name: selectedCluster.topic,
				data: selectedCluster.searchVolumeHistory || [],
			},
		];
	}, [selectedCluster]);

	const keywordSeries = useMemo(() => {
		if (selectedIdea?.searchVolume) {
			return [{ name: selectedIdea.text, data: selectedIdea.searchVolume }];
		}
		if (!selectedCluster) return [];
		return (selectedCluster.keywords || []).slice(0, 8).map((text) => {
			const idea = ideaByText.get(String(text).toLowerCase());
			return { name: text, data: idea?.searchVolume || [] };
		});
	}, [selectedCluster, selectedIdea, ideaByText]);

	const overviewSeries = useMemo(
		() =>
			[...ideas]
				.sort((a, b) => Number(b.latestSearchVolume || 0) - Number(a.latestSearchVolume || 0))
				.slice(0, 5)
				.map((idea) => ({ name: idea.text, data: idea.searchVolume || [] })),
		[ideas],
	);

	useEffect(() => {
		setSelectedCluster((current) => current || clusters[0] || null);
	}, [clusters]);

	const selectCluster = (cluster) => {
		setSelectedCluster(cluster);
		setSelectedIdea(null);
	};

	const getServerSideData = useCallback(
		async (limit, offset, filters = {}) => {
			let rows = tableIdeas;
			const keyword = String(filters.text || "").trim().toLowerCase();
			if (keyword) {
				rows = rows.filter((row) => String(row.text || "").toLowerCase().includes(keyword));
			}
			const competition = String(filters.competition || "").trim().toLowerCase();
			if (competition) {
				rows = rows.filter((row) => {
					const label = formatCompetition(row.competition).toLowerCase();
					return label.includes(competition) || String(row.competition || "").toLowerCase().includes(competition);
				});
			}
			return {
				rows: rows.slice(offset, offset + limit),
				total_rows_count: rows.length,
			};
		},
		[tableIdeas],
	);

	const columns = useMemo(
		() => [
			{
				key: "text",
				name: "Keyword",
				minWidth: 200,
				flex: 1.6,
				renderHeader: headerWithInfo(COLUMN_HELP.text),
				renderCell: ({ row, value }) => (
					<Typography
						fontSize={13}
						fontWeight={selectedIdea?.text === row.text ? 700 : 500}
						color={selectedIdea?.text === row.text ? "primary.main" : grey[900]}>
						{value}
					</Typography>
				),
			},
			{
				key: "latestSearchVolume",
				name: "Latest vol",
				minWidth: 110,
				flex: 0.7,
				align: "right",
				renderHeader: headerWithInfo(COLUMN_HELP.latestSearchVolume),
				valueGetter: ({ row }) => Number(row.latestSearchVolume || 0),
				renderCell: ({ value }) => Number(value || 0).toLocaleString(),
			},
			{
				key: "avgMonthlySearches",
				name: "Avg monthly",
				minWidth: 110,
				flex: 0.7,
				align: "right",
				renderHeader: headerWithInfo(COLUMN_HELP.avgMonthlySearches),
				valueGetter: ({ row }) => Number(row.avgMonthlySearches || 0),
				renderCell: ({ value }) => Number(value || 0).toLocaleString(),
			},
			{
				key: "growthYoY",
				name: "YoY",
				minWidth: 90,
				flex: 0.55,
				align: "right",
				renderHeader: headerWithInfo(COLUMN_HELP.growthYoY),
				valueGetter: ({ row }) => row.growthYoY,
				renderCell: GrowthCell,
			},
			{
				key: "growthMoM",
				name: "MoM",
				minWidth: 90,
				flex: 0.55,
				align: "right",
				renderHeader: headerWithInfo(COLUMN_HELP.growthMoM),
				valueGetter: ({ row }) => row.growthMoM,
				renderCell: GrowthCell,
			},
			{
				key: "growthLatestVsAvg",
				name: "Latest vs avg",
				minWidth: 120,
				flex: 0.7,
				align: "right",
				renderHeader: headerWithInfo(COLUMN_HELP.growthLatestVsAvg),
				valueGetter: ({ row }) => row.growthLatestVsAvg,
				renderCell: GrowthCell,
			},
			{
				key: "growthLatestVsMax",
				name: "Latest vs max",
				minWidth: 120,
				flex: 0.7,
				align: "right",
				renderHeader: headerWithInfo(COLUMN_HELP.growthLatestVsMax),
				valueGetter: ({ row }) => row.growthLatestVsMax,
				renderCell: GrowthCell,
			},
			{
				key: "growthThreeMonthsVsAvg",
				name: "3m vs avg",
				minWidth: 110,
				flex: 0.65,
				align: "right",
				renderHeader: headerWithInfo(COLUMN_HELP.growthThreeMonthsVsAvg),
				valueGetter: ({ row }) => row.growthThreeMonthsVsAvg,
				renderCell: GrowthCell,
			},
			{
				key: "competition",
				name: "Competition",
				minWidth: 130,
				flex: 0.7,
				renderHeader: headerWithInfo(COLUMN_HELP.competition),
				valueGetter: ({ row }) => formatCompetition(row.competition) || row.competition,
				renderCell: ({ value }) => {
					const label = formatCompetition(value) || "-";
					return (
						<Chip
							size="small"
							label={label}
							sx={{
								height: 22,
								fontWeight: 600,
								fontSize: 11.5,
								textTransform: "capitalize",
								...competitionChipSx(label),
							}}
						/>
					);
				},
			},
			{
				key: "competitionIndex",
				name: "Comp. index (0–100)",
				minWidth: 110,
				flex: 0.6,
				align: "right",
				renderHeader: headerWithInfo(COLUMN_HELP.competitionIndex),
				valueGetter: ({ row }) => pickField(row, "competitionIndex", "competition_index"),
				renderCell: ({ value }) => (
					<Typography fontSize={13} textAlign="right">
						{value == null || value === "" ? "-" : Number(value)}
					</Typography>
				),
			},
			{
				key: "lowTopOfPageBidMicros",
				name: "Low bid",
				minWidth: 110,
				flex: 0.65,
				align: "right",
				renderHeader: headerWithInfo(COLUMN_HELP.lowTopOfPageBid),
				valueGetter: ({ row }) => pickField(row, "lowTopOfPageBidMicros", "low_top_of_page_bid_micros"),
				renderCell: MoneyCell,
			},
			{
				key: "highTopOfPageBidMicros",
				name: "High bid",
				minWidth: 110,
				flex: 0.65,
				align: "right",
				renderHeader: headerWithInfo(COLUMN_HELP.highTopOfPageBid),
				valueGetter: ({ row }) => pickField(row, "highTopOfPageBidMicros", "high_top_of_page_bid_micros"),
				renderCell: MoneyCell,
			},
			{
				key: "averageCpcMicros",
				name: "Avg CPC",
				minWidth: 110,
				flex: 0.65,
				align: "right",
				renderHeader: headerWithInfo(COLUMN_HELP.averageCpc),
				valueGetter: ({ row }) => pickField(row, "averageCpcMicros", "average_cpc_micros"),
				renderCell: MoneyCell,
			},
		],
		[selectedIdea],
	);

	if (!ideas.length) {
		return <Alert severity="info">This run has no keyword data yet.</Alert>;
	}

	return (
		<Stack spacing={2}>
			<Stack direction="row" alignItems="flex-end" justifyContent="space-between" flexWrap="wrap" gap={1}>
				<Box>
					<TextField
						select
						size="small"
						label="Growth metric"
						value={growthMetric}
						onChange={(e) => setGrowthMetric(e.target.value)}
						sx={{ ...fieldSx, minWidth: 280 }}
						helperText={metric.help}>
						{GROWTH_METRICS.map((item) => (
							<MenuItem key={item.id} value={item.id}>
								{item.name}
							</MenuItem>
						))}
					</TextField>
				</Box>
				<Stack direction="row" alignItems="center" spacing={1}>
					<Button
						startIcon={<FileDownloadRounded sx={{ fontSize: 16 }} />}
						onClick={() => downloadExploreExcel({ runName: run?.name, ideas, clusters })}
						disabled={!ideas.length}
						sx={quietBtnSx}>
						Download Excel
					</Button>
					<MetricGuideButton sx={quietBtnSx} />
					{(selectedCluster || selectedIdea) && (
						<Button
							onClick={() => {
								setSelectedCluster(null);
								setSelectedIdea(null);
								setClusterOnly(false);
							}}
							sx={quietBtnSx}>
							Clear selection
						</Button>
					)}
				</Stack>
			</Stack>

			{usedFallbackClusters && (
				<Alert severity="info">
					This run has keyword ideas but no Gemini clusters. Topics below are grouped from the keywords so the bubble
					chart still works. Rerun Explore after Gemini is set on the Explore function to get real clusters.
				</Alert>
			)}

			{overviewSeries.length > 0 && (
				<Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
					<Typography fontWeight={700} mb={0.5}>
						Top keywords
					</Typography>
					<Typography variant="body2" color="text.secondary" mb={1}>
						Highest latest-month volume. Click a cluster or table row to change the charts below.
					</Typography>
					<GigaVolumeChart labels={chartLabels} series={overviewSeries} height={260} />
				</Paper>
			)}

			{seedSeries.length > 0 && (
				<Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
					<Typography fontWeight={700} mb={0.5}>
						Seed keywords
					</Typography>
					<Typography variant="body2" color="text.secondary" mb={1}>
						Volume history for the seeds used on this run.
					</Typography>
					<GigaVolumeChart labels={chartLabels} series={seedSeries} height={260} />
				</Paper>
			)}

			{clusters.length > 0 && (
				<Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
					<Stack direction="row" alignItems="center" spacing={0.75} mb={0.5}>
						<Typography fontWeight={700}>Clusters</Typography>
						<InfoTip title="Topics Gemini built from Keyword Planner ideas. Bubble X is volume, Y is the growth metric above, size is volume." />
					</Stack>
					<Typography variant="body2" color="text.secondary" mb={1}>
						Bubble size is search volume. Y-axis is {metric.name.toLowerCase()}. Click a bubble or card.
					</Typography>
					<GigaClusterBubbleChart
						clusters={clusters}
						growthField={metric.field}
						onSelect={selectCluster}
					/>
					<Stack direction="row" flexWrap="wrap" gap={1.5} mt={2}>
						{clusters.map((cluster, index) => {
							const active = selectedCluster?.topic === cluster.topic && !selectedIdea;
							return (
								<Paper
									key={cluster.topic}
									onClick={() => selectCluster(cluster)}
									variant="outlined"
									sx={{
										p: 1.5,
										minWidth: 220,
										flex: "1 1 220px",
										cursor: "pointer",
										borderColor: active ? GIGA_PALETTE[index % GIGA_PALETTE.length] : grey[200],
										borderWidth: active ? 2 : 1,
										bgcolor: active ? "#f8fbff" : "white",
									}}>
									<Typography fontWeight={700}>{cluster.topic}</Typography>
					<Typography variant="caption" color="text.secondary" component="div">
						<Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
							<span>Vol {Number(cluster.searchVolume || 0).toLocaleString()}</span>
							<InfoTip title="Combined search volume of keywords in this topic." />
							<span>· {metric.name}</span>
							<span style={{ color: growthColor(cluster[metric.field]), fontWeight: 700 }}>
								{pct(cluster[metric.field])}
							</span>
							<InfoTip title={metric.help} />
						</Stack>
					</Typography>
									<Typography variant="body2" mt={1} color="text.secondary">
										{(cluster.keywords || []).join(", ")}
									</Typography>
								</Paper>
							);
						})}
					</Stack>
				</Paper>
			)}

			{selectedCluster && (
				<Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: "primary.light" }}>
					<Stack direction="row" alignItems="center" justifyContent="space-between" mb={1} gap={1}>
						<Typography fontWeight={700}>Details: {selectedCluster.topic}</Typography>
						<Button onClick={() => setSelectedCluster(null)} sx={quietBtnSx}>
							Close
						</Button>
					</Stack>
					<Typography variant="body2" color="text.secondary" mb={2} component="div">
						<Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
							<span>Volume {Number(selectedCluster.searchVolume || 0).toLocaleString()}</span>
							<InfoTip title="Combined search volume of keywords in this topic." />
							<span>· {metric.name}</span>
							<span style={{ color: growthColor(selectedCluster[metric.field]), fontWeight: 700 }}>
								{pct(selectedCluster[metric.field])}
							</span>
							<InfoTip title={metric.help} />
						</Stack>
					</Typography>
					<Typography fontWeight={600} mb={0.5}>
						Cluster search volume
					</Typography>
					<GigaVolumeChart labels={chartLabels} series={clusterSeries} height={280} />
					<Typography fontWeight={600} mt={2} mb={0.5}>
						{selectedIdea ? `Keyword: ${selectedIdea.text}` : "Keyword search volume"}
					</Typography>
					<GigaVolumeChart labels={chartLabels} series={keywordSeries} height={300} />
				</Paper>
			)}

			{!selectedCluster && selectedIdea && (
				<Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
					<Typography fontWeight={700} mb={0.5}>
						Keyword: {selectedIdea.text}
					</Typography>
					<GigaVolumeChart labels={chartLabels} series={keywordSeries} height={280} />
				</Paper>
			)}

			<Box
				sx={{
					height: 520,
					border: `1px solid ${grey[200]}`,
					borderRadius: 2,
					overflow: "hidden",
					bgcolor: "#fff",
					"& > .MuiPaper-root": {
						height: "100% !important",
						boxShadow: "none",
					},
					"& tbody tr": { cursor: "pointer" },
				}}>
				<TanstackDatagrid
					key={`${clusterOnly ? selectedCluster?.topic || "all" : "all"}-${tableIdeas.length}`}
					columns={columns}
					pageData={pageData}
					setPageData={setPageData}
					getServerSideData={getServerSideData}
					rowsPerPage={50}
					serverSidePagination
					getRowId={(row) => row.text}
					handleRowClick={({ row }) => setSelectedIdea(row)}
					emptyMessage="No keywords match the current filters."
					rowHeight={40}
					headerHeight={44}
					headerRowStyle={{ background: "#FAFAFA" }}>
					<Box>
						<Typography fontWeight={700} fontSize={13.5} color={grey[800]}>
							{clusterOnly && selectedCluster
								? `Keywords in ${selectedCluster.topic} (${tableIdeas.length})`
								: `All keywords (${tableIdeas.length})`}
						</Typography>
						<Stack direction="row" alignItems="center" spacing={1} mt={0.5}>
							<Typography fontSize={11.5} color={grey[500]}>
								Click a row to chart that keyword. Column menu can hide metrics and export Excel.
							</Typography>
							{selectedCluster && (
								<Button size="small" onClick={() => setClusterOnly((value) => !value)} sx={quietBtnSx}>
									{clusterOnly ? "Show all keywords" : "Only this cluster"}
								</Button>
							)}
						</Stack>
					</Box>
				</TanstackDatagrid>
			</Box>
		</Stack>
	);
};

export default GigaExploreView;
